
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from "@/hooks/use-toast";
import { questions } from '@/data/questions';
import { Topic } from '@/components/TopicSelector';
import { topics } from '@/data/topics';
import { getQuestionsForSubTopic } from '@/utils/questionUtils';

interface Question {
  id: string;
  text: string;
  type: 'single' | 'multiple' | 'true-false';
  options: {
    id: string;
    text: string;
    isCorrect: boolean;
  }[];
  explanation?: string;
  topic: string;
}

interface QuizContextType {
  currentQuestion: Question | null;
  currentQuestionIndex: number;
  topics: Topic[];
  currentTopic: string | null;
  currentSubTopic: string | null;
  questionsByTopic: Record<string, Question[]>;
  correctAnswers: number;
  isComplete: boolean;
  topicQuestions: Question[];
  totalQuestionsInTopic: number;
  setCurrentTopic: (topicId: string, subtopicId?: string) => void;
  nextQuestion: () => void;
  handleAnswer: (correct: boolean) => void;
  resetQuiz: () => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

export const QuizProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [currentTopic, setCurrentTopic] = useState<string | null>(null);
  const [currentSubTopic, setCurrentSubTopic] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [questionsByTopic, setQuestionsByTopic] = useState<Record<string, Question[]>>({});
  const [topicQuestions, setTopicQuestions] = useState<Question[]>([]);
  const [totalQuestionsInTopic, setTotalQuestionsInTopic] = useState(0);
  const { toast } = useToast();

  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const randomizeQuestionOptions = (question: Question): Question => {
    return {
      ...question,
      options: shuffleArray(question.options)
    };
  };

  useEffect(() => {
    const groupedQuestions: Record<string, Question[]> = {};
    
    questions.forEach(question => {
      if (!groupedQuestions[question.topic]) {
        groupedQuestions[question.topic] = [];
      }
      groupedQuestions[question.topic].push(question as Question);
    });
    
    setQuestionsByTopic(groupedQuestions);
  }, []);

  useEffect(() => {
    if (currentTopic && questionsByTopic[currentTopic]) {
      let topicQuestionsToUse: Question[];
      
      if (currentTopic === 'taj101' && currentSubTopic) {
        // Get questions for the specific subtopic
        topicQuestionsToUse = getQuestionsForSubTopic(questions, currentTopic, currentSubTopic);
        console.log(`Loading subtopic ${currentSubTopic} with ${topicQuestionsToUse.length} questions`);
      } else {
        topicQuestionsToUse = questionsByTopic[currentTopic];
      }
      
      setTotalQuestionsInTopic(topicQuestionsToUse.length);
      
      const selectedTopic = topics.find(t => t.id === currentTopic);
      const questionsPerSession = selectedTopic?.questionCount || 30;
      
      const randomizedQuestions = shuffleArray(topicQuestionsToUse)
        .map(question => randomizeQuestionOptions(question))
        .slice(0, Math.min(questionsPerSession, topicQuestionsToUse.length));
      
      setTopicQuestions(randomizedQuestions);
      setCurrentQuestionIndex(0);
      setCorrectAnswers(0);
      setIsComplete(false);
    }
  }, [currentTopic, currentSubTopic, questionsByTopic]);

  const currentQuestion = topicQuestions[currentQuestionIndex] || null;

  const handleTopicSelect = (topicId: string, subtopicId?: string) => {
    setCurrentTopic(topicId);
    setCurrentSubTopic(subtopicId || null);
    
    const topicName = topics.find(t => t.id === topicId)?.title;
    let toastMessage = "";
    
    if (subtopicId) {
      const subtopicFormatted = subtopicId
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      toastMessage = `Starting quiz on ${topicName} - ${subtopicFormatted}`;
    } else {
      toastMessage = `Starting quiz on ${topicName}`;
    }
      
    toast({
      title: "Topic selected",
      description: toastMessage,
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < topicQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handleAnswer = (correct: boolean) => {
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setCorrectAnswers(0);
    setIsComplete(false);
    
    if (currentTopic && questionsByTopic[currentTopic]) {
      let topicQuestionsToUse: Question[];
      
      if (currentTopic === 'taj101' && currentSubTopic) {
        topicQuestionsToUse = getQuestionsForSubTopic(questions, currentTopic, currentSubTopic);
      } else {
        topicQuestionsToUse = questionsByTopic[currentTopic];
      }
      
      const selectedTopic = topics.find(t => t.id === currentTopic);
      const questionsPerSession = selectedTopic?.questionCount || 30;
      
      const randomizedQuestions = shuffleArray(topicQuestionsToUse)
        .map(question => randomizeQuestionOptions(question))
        .slice(0, Math.min(questionsPerSession, topicQuestionsToUse.length));
      
      setTopicQuestions(randomizedQuestions);
    }
  };

  const value = {
    currentQuestion,
    currentQuestionIndex,
    topics,
    currentTopic,
    currentSubTopic,
    questionsByTopic,
    correctAnswers,
    isComplete,
    topicQuestions,
    totalQuestionsInTopic,
    setCurrentTopic: handleTopicSelect,
    nextQuestion,
    handleAnswer,
    resetQuiz,
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};
